import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faUser, faChevronLeft, faChevronRight, faCalendar } from "@fortawesome/free-solid-svg-icons";
import { calendarAPI, workingHoursAPI } from "../../../services/calendarService";
import AddForm from "../../../components/Common/AddForm/AddForm";
import { useBreadcrumb } from "../../../contexts/BreadcrumbContext";
import "./Calendar.css";
import "../../PracticeManagement/PracticesList/AddPractice/AddPractice.css";
import { matchesWordStartAny } from "../../../utils/searchMatch";

/** MM/DD/YYYY for appointment form (local calendar day, no UTC shift from toISOString) */
const toAppointmentFormDate = (d) => {
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const y = d.getFullYear();
  return `${String(m).padStart(2, "0")}/${String(day).padStart(2, "0")}/${y}`;
};

const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const mdyToDate = (mdy) => {
  if (!mdy || typeof mdy !== "string") return null;
  const parts = mdy.split("/");
  if (parts.length !== 3) return null;
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (!month || !day || !year) return null;
  return new Date(year, month - 1, day);
};

const mdyToLocalYMD = (mdy) => {
  const d = mdyToDate(mdy);
  return d ? toLocalYMD(d) : "";
};

/** Match API appointment_date to the same local calendar day as the grid (avoids UTC day shift from ISO). */
const appointmentDateToLocalYMD = (raw) => {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) return toLocalYMD(dt);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
};

const Calendar = () => {
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [loading, setLoading] = useState(true);
  const [systemCaregivers, setSystemCaregivers] = useState([]);
  const [selectedCaregivers, setSelectedCaregivers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null); // logged-in user info
  const [calendarPermissions, setCalendarPermissions] = useState({
    create: true,
    view: true,
    edit: true,
    delete: true
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [timeInterval, setTimeInterval] = useState(15);
  const [viewMode, setViewMode] = useState("list"); // week or list - default to list view
  const [workingHours, setWorkingHours] = useState([]);
  /** YYYY-MM-DD (local) -> working hour ranges for that calendar day (week view uses all seven keys). */
  const [workingHoursByDate, setWorkingHoursByDate] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [modalTimeSlotsByDate, setModalTimeSlotsByDate] = useState({});
  const [showNonWorkingHoursModal, setShowNonWorkingHoursModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  const [appointmentData, setAppointmentData] = useState({
    practice: "",
    systemCaregiver: "",
    visitType: "",
    visitDuration: "",
    reasonForAppointment: "",
    patient: "",
    appointmentDate: "",
    appointmentTime: "",
    provider: "",
    facility: "",
    visitStatus: ""
  });
  const [patients, setPatients] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [loadingPracticeData, setLoadingPracticeData] = useState(false);
  const [isPatientDropdownOpen, setIsPatientDropdownOpen] = useState(false);
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const patientDropdownRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editAppointmentData, setEditAppointmentData] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [yearRangeStart, setYearRangeStart] = useState(
    Math.floor(new Date().getFullYear() / 10) * 10
  );
  const monthYearPickerRef = useRef(null);
  const listScheduleContainerRef = useRef(null);
  const weekScheduleContainerRef = useRef(null);
  const [isInitialScheduleLoading, setIsInitialScheduleLoading] = useState(true);
  const hasFinishedInitialScheduleLoadRef = useRef(false);
  const isSystemCaregiver = currentUser?.role_id === 7;
  const canCreateAppointment = !isSystemCaregiver || calendarPermissions.create;
  const canViewAppointments = !isSystemCaregiver || calendarPermissions.view;
  const canEditAppointment = !isSystemCaregiver || calendarPermissions.edit;
  const canDeleteAppointment = !isSystemCaregiver || calendarPermissions.delete;
  useEffect(() => {
    updateBreadcrumbs([
      { label: "Calendar Settings", path: "/calendar-settings/visit-type" },
      { label: "Calendar", path: null, active: true }
    ]);
    return () => {
      clearBreadcrumbs();
    };
  }, [updateBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    const loadCalendarPermissions = async () => {
      if (!isSystemCaregiver || !currentUser?.id) return;
      try {
        const [createRes, viewRes, editRes, deleteRes] = await Promise.all([
          calendarAPI.checkPermission(currentUser.id, "Create").catch(() => ({ hasPermission: false })),
          calendarAPI.checkPermission(currentUser.id, "View").catch(() => ({ hasPermission: false })),
          calendarAPI.checkPermission(currentUser.id, "Edit").catch(() => ({ hasPermission: false })),
          calendarAPI.checkPermission(currentUser.id, "Delete").catch(() => ({ hasPermission: false }))
        ]);
        setCalendarPermissions({
          create: Boolean(createRes?.hasPermission),
          view: Boolean(viewRes?.hasPermission),
          edit: Boolean(editRes?.hasPermission),
          delete: Boolean(deleteRes?.hasPermission)
        });
      } catch (_error) {
        setCalendarPermissions({ create: false, view: false, edit: false, delete: false });
      }
    };
    loadCalendarPermissions();
  }, [currentUser?.id, isSystemCaregiver]);

  useEffect(() => {
    if (isSystemCaregiver && currentUser?.id && !calendarPermissions.view) {
      setSystemCaregivers([]);
      setSelectedCaregivers([]);
      setAppointments([]);
    }
  }, [isSystemCaregiver, currentUser?.id, calendarPermissions.view]);

  // Fetch initial data
  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);

      // Detect logged-in user for role-based behavior
      let loggedInUser = null;
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const parsed = JSON.parse(userStr);
          loggedInUser = parsed?.user || parsed;
          setCurrentUser(loggedInUser);
        }
      } catch (e) { /* ignore */ }

      const response = await calendarAPI.getCalendarView();
      if (response.success) {
        const caregivers = response.data.systemCaregivers || [];
        setSystemCaregivers(caregivers);

        // For System Caregiver (role 7): auto-select themselves, skip saved selection
        if (loggedInUser?.role_id === 7) {
          const selfId = parseInt(loggedInUser.id);
          setSelectedCaregivers([selfId]);
        } else {
          // Load selected caregivers from storage for other roles
          try {
            const userId = loggedInUser?.id;
            if (userId) {
              try {
                const selectedRes = await calendarAPI.getSelectedCaregivers(userId);
                if (selectedRes.success && selectedRes.caregiver_ids) {
                  setSelectedCaregivers(selectedRes.caregiver_ids.map(id => parseInt(id)));
                }
              } catch (err) {
                // No selected caregivers yet
              }
            }
          } catch (err) { /* ignore */ }
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch calendar data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch working hours and generate time slots (per calendar day; week view loads all seven days)
  const fetchWorkingHoursAndSlots = useCallback(async () => {
    if (!selectedDate) return;
    try {
      if (viewMode === "list") {
        const dateStr = toLocalYMD(selectedDate);
        const [response, workingHoursRes] = await Promise.all([
          calendarAPI.getAppointmentTimeSchedule(dateStr, timeInterval),
          calendarAPI.getActualWorkingHours(dateStr)
        ]);
        if (response.success) {
          setTimeSlots(response.time_slots || []);
        }
        const wh = workingHoursRes.success ? (workingHoursRes.working_hours || []) : [];
        setWorkingHours(wh);
        setWorkingHoursByDate({ [dateStr]: wh });
      } else {
        const anchor = new Date(selectedDate);
        const dow = anchor.getDay();
        const diff = anchor.getDate() - dow + (dow === 0 ? -6 : 1);
        const monday = new Date(anchor);
        monday.setDate(diff);
        const weekDates = [];
        for (let i = 0; i < 7; i += 1) {
          const d = new Date(monday);
          d.setDate(monday.getDate() + i);
          weekDates.push(d);
        }
        const results = await Promise.all(
          weekDates.map(async (d) => {
            const ds = toLocalYMD(d);
            const [slotsRes, whRes] = await Promise.all([
              calendarAPI.getAppointmentTimeSchedule(ds, timeInterval),
              calendarAPI.getActualWorkingHours(ds)
            ]);
            return {
              ds,
              slots: slotsRes.success ? slotsRes.time_slots || [] : [],
              wh: whRes.success ? whRes.working_hours || [] : []
            };
          })
        );
        const map = {};
        results.forEach((r) => {
          map[r.ds] = r.wh;
        });
        setWorkingHoursByDate(map);
        const currentStr = toLocalYMD(selectedDate);
        const pick = results.find((r) => r.ds === currentStr) || results[0];
        setTimeSlots(pick?.slots || []);
        setWorkingHours(map[currentStr] || []);
      }
    } catch (error) {
      console.error("Error fetching working hours:", error);
    }
  }, [selectedDate, timeInterval, viewMode]);

  // Fetch patients, providers, facilities, and practice-scoped visit types when practice is selected
  const fetchPracticeData = useCallback(async (practiceId, caregiverId = null) => {
    if (!practiceId) {
      setPatients([]);
      setProviders([]);
      setFacilities([]);
      setVisitTypes([]);
      setLoadingPracticeData(false);
      return;
    }

    setLoadingPracticeData(true);
    try {
      const [practiceDataRes, providersFacilitiesRes] = await Promise.all([
        calendarAPI.getSystemCaregiversOfPracticeAndPatient(practiceId, caregiverId),
        workingHoursAPI.getProvidersAndFacility(practiceId).catch(() => ({ success: true, providers: [], addresses: [] }))
      ]);

      if (practiceDataRes.success) {
        setPatients(practiceDataRes.patients || []);
        const vt = practiceDataRes.visit_types;
        setVisitTypes(Array.isArray(vt) ? vt : []);
      }

      if (providersFacilitiesRes.success) {
        setProviders(providersFacilitiesRes.providers || []);
        setFacilities(providersFacilitiesRes.addresses || []);
      }
    } catch (error) {
      console.error("Error fetching practice data:", error);
      toast.error("Failed to load practice data");
      setVisitTypes([]);
    } finally {
      setLoadingPracticeData(false);
    }
  }, []);

  // Fetch practices on mount
  const fetchPractices = useCallback(async (caregiverId = null) => {
    try {
      const response = await calendarAPI.fetchPractice(caregiverId);
      if (response.success) {
        setPractices(response.practices || []);
        // Don't auto-select - let user select practice
      }
    } catch (error) {
      console.error("Error fetching practices:", error);
    }
  }, []);

  // Get week days helper (moved before fetchAppointments)
  const getWeekDaysHelper = useCallback(() => {
    const startOfWeek = new Date(selectedDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(startOfWeek.setDate(diff));
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  }, [selectedDate]);

  // Fetch appointments for date range
  const fetchAppointments = useCallback(async () => {
    const isSystemCaregiver = currentUser?.role_id === 7;
    // For role 7, backend auto-filters by their session userId — no caregiver IDs needed
    if (!selectedDate || (!isSystemCaregiver && selectedCaregivers.length === 0)) {
      setAppointments([]);
      return;
    }

    try {
      let startDate, endDate;
      if (viewMode === 'week') {
        const weekDays = getWeekDaysHelper();
        startDate = weekDays[0];
        endDate = weekDays[6];
      } else {
        startDate = selectedDate;
        endDate = selectedDate;
      }

      const startDateStr = toLocalYMD(startDate);
      const endDateStr = toLocalYMD(endDate);

      // For System Caregiver: send their own ID; backend also enforces this server-side
      const caregiverIdsToSend = isSystemCaregiver
        ? (currentUser?.id ? [parseInt(currentUser.id)] : selectedCaregivers)
        : selectedCaregivers;

      // Do NOT pass practice_id — it adds p.practice_id = ? to WHERE, which can exclude
      // appointments when a patient's practice doesn't match the calendar's selectedPracticeId
      const response = await calendarAPI.getAppointments(
        startDateStr,
        endDateStr,
        caregiverIdsToSend,
        null  // always null — filter by caregiver + date only
      );

      if (response && response.success) {
        const apts = response.appointments || [];
        setAppointments(apts);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      setAppointments([]);
    }
  }, [selectedDate, selectedCaregivers, viewMode, getWeekDaysHelper, currentUser]);

  /** Load working hours / slot definitions for the range first, then appointments — keeps availability + grid in sync. */
  const refreshScheduleForSelectedRange = useCallback(async () => {
    try {
      await fetchWorkingHoursAndSlots();
      await fetchAppointments();
    } finally {
      if (!hasFinishedInitialScheduleLoadRef.current) {
        hasFinishedInitialScheduleLoadRef.current = true;
        setIsInitialScheduleLoading(false);
      }
    }
  }, [fetchWorkingHoursAndSlots, fetchAppointments]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  useEffect(() => {
    // Wait until user is resolved to avoid transient "all practices" fetch.
    if (!currentUser) return;
    // For SuperAdmin, booking modal should always load caregiver-filtered practices only.
    if (currentUser.role_id === 1) {
      setPractices([]);
      return;
    }
    fetchPractices();
  }, [currentUser, fetchPractices]);

  useEffect(() => {
    if (!showAppointmentModal) return;
    if (currentUser?.role_id !== 1) return;

    const selectedCg = appointmentData.systemCaregiver || null;
    // Prevent showing stale/unfiltered practices while caregiver-scoped fetch runs.
    setPractices([]);
    if (!selectedCg) return;
    fetchPractices(selectedCg);
  }, [showAppointmentModal, currentUser?.role_id, appointmentData.systemCaregiver, fetchPractices]);

  // Load patients, providers, facilities, and visit types for the appointment form when the modal opens or practice changes
  useEffect(() => {
    if (!showAppointmentModal) return;
    const pid = appointmentData.practice;
    if (!pid) {
      setVisitTypes([]);
      return;
    }
    const selectedCaregiverId = currentUser?.role_id === 7
      ? currentUser?.id
      : appointmentData.systemCaregiver;
    fetchPracticeData(parseInt(pid, 10), selectedCaregiverId || null);
  }, [showAppointmentModal, appointmentData.practice, appointmentData.systemCaregiver, fetchPracticeData, currentUser]);

  // Default system caregiver in the form if still empty once caregivers are loaded
  useEffect(() => {
    if (!showAppointmentModal) return;
    setAppointmentData((prev) => {
      if (prev.systemCaregiver) return prev;
      if (systemCaregivers.length === 0) return prev;
      const pick =
        currentUser?.role_id === 7 && currentUser?.id
          ? String(currentUser.id)
          : String(systemCaregivers[0].id);
      return { ...prev, systemCaregiver: pick };
    });
  }, [showAppointmentModal, systemCaregivers, currentUser]);

  useEffect(() => {
    void refreshScheduleForSelectedRange();
  }, [refreshScheduleForSelectedRange]);

  // Save selected caregivers
  const saveSelectedCaregivers = useCallback(async (caregiverIds) => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        const userId = user?.id || user?.user?.id;
        if (userId) {
          try {
            await calendarAPI.updateSelectedCaregivers(caregiverIds, userId);
          } catch (error) {
            console.error("Error saving selected caregivers:", error);
          }
        }
      }
    } catch (err) {
      // User not in localStorage
    }
  }, []);

  const toggleCaregiver = (caregiverId) => {
    const id = parseInt(caregiverId); // Always normalize to integer
    setSelectedCaregivers((prev) => {
      let newSelection;
      if (prev.map(x => parseInt(x)).includes(id)) {
        newSelection = prev.filter((x) => parseInt(x) !== id);
      } else {
        newSelection = [...prev, id];
      }
      saveSelectedCaregivers(newSelection);
      return newSelection;
    });
  };

  const handleDateClick = (date) => {
    if (date) {
      setSelectedDate(new Date(date));
    }
  };

  const handleTimeSlotClick = (timeSlot, caregiverId, cellDate = selectedDate) => {
    if (!canCreateAppointment) {
      toast.error("You do not have permission to create appointments.");
      return;
    }

    const [time, period] = timeSlot.split(' ');
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    const time24 = `${String(hours).padStart(2, '0')}:${minutes}`;

    const bookingDay = cellDate ? new Date(cellDate) : new Date(selectedDate);
    const isAvailable = isTimeSlotAvailable(time24, bookingDay);

    const formattedDate = toAppointmentFormDate(bookingDay);

    const defaultSystemCaregiver = (() => {
      if (caregiverId != null && caregiverId !== "") return String(caregiverId);
      if (currentUser?.role_id === 7 && currentUser?.id) return String(currentUser.id);
      if (systemCaregivers.length > 0) return String(systemCaregivers[0].id);
      return "";
    })();

    const appointmentDataObj = {
      practice: selectedPracticeId ? selectedPracticeId.toString() : "",
      systemCaregiver: defaultSystemCaregiver,
      visitType: "",
      visitDuration: "",
      reasonForAppointment: "",
      patient: "",
      appointmentDate: formattedDate,
      appointmentTime: timeSlot,
      provider: "",
      facility: "",
      visitStatus: ""
    };

    setSelectedTimeSlot({ time: timeSlot, time24, caregiverId });

    // If clicking on non-working hours (white/unavailable), show confirmation modal
    if (!isAvailable) {
      setPendingAppointmentData(appointmentDataObj);
      setShowNonWorkingHoursModal(true);
    } else {
      // If clicking on working hours, show appointment form directly
      setAppointmentData(appointmentDataObj);
      setShowAppointmentModal(true);
    }
  };

  const handleConfirmNonWorkingHours = () => {
    // User confirmed, proceed with booking in non-working hours
    setShowNonWorkingHoursModal(false);
    if (pendingAppointmentData) {
      setAppointmentData(pendingAppointmentData);
      setShowAppointmentModal(true);
    }
    setPendingAppointmentData(null);
  };

  const handleCancelNonWorkingHours = () => {
    setShowNonWorkingHoursModal(false);
    setPendingAppointmentData(null);
    setSelectedTimeSlot(null);
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedTimeSlot(null);
    setModalTimeSlotsByDate({});
    setAppointmentData({
      practice: "",
      systemCaregiver: "",
      visitType: "",
      visitDuration: "",
      reasonForAppointment: "",
      patient: "",
      appointmentDate: "",
      appointmentTime: "",
      provider: "",
      facility: "",
      visitStatus: ""
    });
  };

  const closeEditAppointmentModal = () => {
    setShowEditAppointmentModal(false);
    setEditingAppointment(null);
    setEditAppointmentData(null);
  };

  const ymdToMdy = (ymd) => {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd))) return "";
    const [year, month, day] = String(ymd).split("-");
    return `${month}/${day}/${year}`;
  };

  const openEditAppointmentModal = (appointment) => {
    setEditingAppointment(appointment);
    setEditAppointmentData({
      id: appointment.id,
      practice: appointment.practice_id ? String(appointment.practice_id) : "",
      patient: appointment.patient_id ? String(appointment.patient_id) : "",
      visitType: appointment.visit_type_id ? String(appointment.visit_type_id) : "",
      systemCaregiver: appointment.caregiver_id ? String(appointment.caregiver_id) : "",
      appointmentDate: ymdToMdy(appointmentDateToLocalYMD(appointment.appointment_date)),
      appointmentTime: convertTo12Hour(normalizeAppointmentTimeTo24(appointment.appointment_time)),
      visitDuration: appointment.duration ? String(appointment.duration) : "30",
      confirmation: String(appointment?.status || "").toLowerCase() === "confirmed" ? "yes" : "no"
    });
    setShowEditAppointmentModal(true);
  };

  const handleUpdateAppointment = async () => {
    if (!canEditAppointment) {
      toast.error("You do not have permission to update appointments.");
      return;
    }
    if (!editAppointmentData?.id) return;
    if (!editAppointmentData.practice || !editAppointmentData.patient || !editAppointmentData.visitType || !editAppointmentData.systemCaregiver || !editAppointmentData.appointmentDate || !editAppointmentData.appointmentTime) {
      toast.error("Missing appointment data. Please refresh and try again.");
      return;
    }

    try {
      const payload = {
        practice_id: parseInt(editAppointmentData.practice, 10),
        patient_id: parseInt(editAppointmentData.patient, 10),
        visit_type_id: parseInt(editAppointmentData.visitType, 10),
        system_caregiver_id: parseInt(editAppointmentData.systemCaregiver, 10),
        appointment_date: editAppointmentData.appointmentDate,
        appointment_time: editAppointmentData.appointmentTime,
        visit_duration: parseInt(editAppointmentData.visitDuration, 10) || 30,
        confirmation: editAppointmentData.confirmation || "no",
        visit_status: editAppointmentData.confirmation === "yes" ? "confirmed" : "scheduled"
      };

      const response = await calendarAPI.updateAppointment(editAppointmentData.id, payload);
      if (response?.success) {
        toast.success("Appointment updated successfully");
        closeEditAppointmentModal();
        await refreshScheduleForSelectedRange();
      } else {
        throw new Error(response?.message || "Failed to update appointment");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update appointment");
    }
  };

  const handleDeleteAppointment = async () => {
    if (!canDeleteAppointment) {
      toast.error("You do not have permission to delete appointments.");
      return;
    }
    if (!editAppointmentData?.id) return;
    try {
      const response = await calendarAPI.deleteAppointment(editAppointmentData.id);
      if (response?.success) {
        toast.success("Appointment deleted successfully");
        closeEditAppointmentModal();
        await refreshScheduleForSelectedRange();
      } else {
        throw new Error(response?.message || "Failed to delete appointment");
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete appointment");
    }
  };

  const handleCreateAppointment = async () => {
    // Validate required fields
    if (!appointmentData.practice || !appointmentData.systemCaregiver || !appointmentData.visitType ||
      !appointmentData.patient || !appointmentData.appointmentDate || !appointmentData.appointmentTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Prepare appointment data for API
      const appointmentPayload = {
        practice_id: parseInt(appointmentData.practice),
        patient_id: parseInt(appointmentData.patient),
        visit_type_id: parseInt(appointmentData.visitType),
        system_caregiver_id: currentUser?.role_id === 7 && currentUser?.id
          ? parseInt(currentUser.id, 10)
          : parseInt(appointmentData.systemCaregiver, 10),
        appointment_date: appointmentData.appointmentDate, // MM/DD/YYYY format
        appointment_time: appointmentData.appointmentTime, // "10:15 AM" format
        visit_duration: appointmentData.visitDuration || "30",
        reason_for_appointment: appointmentData.reasonForAppointment || null,
        provider_id: appointmentData.provider ? parseInt(appointmentData.provider) : null,
        facility_id: appointmentData.facility ? parseInt(appointmentData.facility) : null,
        visit_status: appointmentData.visitStatus || "scheduled"
      };

      const response = await calendarAPI.createAppointment(appointmentPayload);

      if (response.success) {
        toast.success("Appointment created successfully");
        setShowAppointmentModal(false);
        setSelectedTimeSlot(null);
        setModalTimeSlotsByDate({});
        setAppointmentData({
          practice: "",
          systemCaregiver: "",
          visitType: "",
          visitDuration: "",
          reasonForAppointment: "",
          patient: "",
          appointmentDate: "",
          appointmentTime: "",
          provider: "",
          facility: "",
          visitStatus: ""
        });
        // Refresh working hours + appointment grid together
        await refreshScheduleForSelectedRange();
        // Optionally refresh calendar view to show new appointment
        if (selectedPracticeId) {
          fetchPracticeData(selectedPracticeId);
        }
      } else {
        throw new Error(response.message || "Failed to create appointment");
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error(error.message || "Failed to create appointment");
    }
  };

  // Handle visit type change - auto-fill duration
  const handleVisitTypeChange = (visitTypeId) => {
    if (!visitTypeId) {
      setAppointmentData({
        ...appointmentData,
        visitType: "",
        visitDuration: ""
      });
      return;
    }

    const selectedVisitType = visitTypes.find(
      (vt) => Number(vt.id) === parseInt(visitTypeId, 10)
    );
    setAppointmentData({
      ...appointmentData,
      visitType: visitTypeId,
      visitDuration: selectedVisitType ? (selectedVisitType.duration || "") : ""
    });
  };

  // Handle practice change in appointment modal
  const handlePracticeChangeInModal = (practiceId) => {
    const practiceIdNum = practiceId ? parseInt(practiceId) : null;

    setAppointmentData({
      ...appointmentData,
      practice: practiceId || "",
      patient: "",
      visitType: "",
      visitDuration: ""
    });

    if (!practiceIdNum) {
      setPatients([]);
      setProviders([]);
      setFacilities([]);
      setVisitTypes([]);
      setLoadingPracticeData(false);
    }
  };

  const handleSystemCaregiverChangeInModal = (caregiverId) => {
    const isCaregiverLockedByContext =
      currentUser?.role_id === 7 || Boolean(selectedTimeSlot?.caregiverId);
    if (isCaregiverLockedByContext) return;

    setAppointmentData((prev) => ({
      ...prev,
      systemCaregiver: caregiverId,
      practice: "",
      patient: ""
    }));
    if (currentUser?.role_id === 1) {
      setVisitTypes([]);
      setPatients([]);
    }
  };

  // Filter patients based on search term
  const filteredPatients = patients.filter((patient) => {
    if (!patientSearchTerm.trim()) return true;
    const fullName = `${patient.first_name} ${patient.last_name}`;
    return matchesWordStartAny(
      [fullName, patient.first_name, patient.last_name],
      patientSearchTerm
    );
  });

  // Close patient dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target)) {
        setIsPatientDropdownOpen(false);
      }
      if (monthYearPickerRef.current && !monthYearPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
        setShowYearPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // Calendar helpers
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Convert JS day index (Sun=0..Sat=6) to Monday-first index (Mon=0..Sun=6)
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];

    // Previous month days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonthLastDay - i));
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Next month days to fill the grid
    const remaining = 42 - days.length;
    for (let day = 1; day <= remaining; day++) {
      days.push(new Date(year, month + 1, day));
    }

    return days;
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date) => {
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const decadeYears = Array.from({ length: 10 }, (_, i) => yearRangeStart + i);
  const shortMonthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ];

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
  };

  const clearSelection = () => {
    setSelectedDate(new Date());
  };

  // Get week days (Monday to Sunday) starting from selected date
  const getWeekDays = () => {
    const startOfWeek = new Date(selectedDate);
    const day = startOfWeek.getDay();
    // Adjust to Monday (day 1). If Sunday (day 0), go back 6 days
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(startOfWeek.setDate(diff));

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  };

  // Format date header like "Dec 8, 2025 Mon"
  const normalizeAppointmentTimeTo24 = (timeValue) => {
    if (!timeValue) return '';
    const raw = String(timeValue).trim();

    // Supports "HH:MM" and "HH:MM:SS"
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
      return raw.substring(0, 5);
    }

    // Supports "H:MM AM/PM" or "HH:MM AM/PM"
    const twelveHourMatch = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (twelveHourMatch) {
      let h = parseInt(twelveHourMatch[1], 10);
      const m = parseInt(twelveHourMatch[2], 10);
      const meridiem = twelveHourMatch[3].toUpperCase();
      if (meridiem === 'PM' && h !== 12) h += 12;
      if (meridiem === 'AM' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    return '';
  };

  const addMinutesToTime24 = (time24, minsToAdd) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const total = (h * 60) + m + (Number(minsToAdd) || 0);
    const normalized = ((total % 1440) + 1440) % 1440;
    const endH = Math.floor(normalized / 60);
    const endM = normalized % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  };

  // Get appointments for a specific time slot and caregiver
  const getAppointmentsForSlot = (date, timeSlot, caregiverId) => {
    if (!appointments || appointments.length === 0) return [];

    const dateStr = toLocalYMD(date);
    const [time, period] = timeSlot.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    const time24 = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    return appointments.filter(apt => {
      const aptDate = appointmentDateToLocalYMD(apt.appointment_date);
      const aptTime = normalizeAppointmentTimeTo24(apt.appointment_time);
      // caregiver_id is the actual DB column; compare as integers
      return aptDate === dateStr &&
        aptTime === time24 &&
        parseInt(apt.caregiver_id) === parseInt(caregiverId);
    });
  };

  // Convert 24-hour time to 12-hour format for display
  const convertTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    let h = hours % 12;
    if (h === 0) h = 12;
    return `${h}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const formatDateHeader = (date) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const dayName = dayNames[date.getDay()];
    return `${month} ${day}, ${year} ${dayName}`;
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const weekDays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  const calendarDays = getCalendarDays();
  const selectedCaregiversList = systemCaregivers.filter(cg => selectedCaregivers.map(id => parseInt(id)).includes(parseInt(cg.id)));

  // Always show full-day slots; working hours are highlighted via availability styling.
  const displayTimeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 0; hour <= 23; hour += 1) {
      for (let min = 0; min < 60; min += timeInterval) {
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        slots.push(`${displayHour}:${String(min).padStart(2, "0")} ${period}`);
      }
    }
    return slots;
  }, [timeInterval]);

  useEffect(() => {
    const firstWorkingStart = (() => {
      if (!workingHours || workingHours.length === 0) return "9:00 AM";
      const validStarts = workingHours
        .map((wh) => wh?.start_time)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      if (validStarts.length === 0) return "9:00 AM";
      return convertTo12Hour(validStarts[0]);
    })();

    const slotIndex = displayTimeSlots.findIndex((slot) => slot === firstWorkingStart);
    if (slotIndex < 0) return;

    const targetOffset = slotIndex * 40;
    const targetContainer =
      viewMode === "list" ? listScheduleContainerRef.current : weekScheduleContainerRef.current;

    if (targetContainer) {
      targetContainer.scrollTop = targetOffset;
    }
  }, [viewMode, selectedDate, timeInterval, workingHours]);

  // Check if a time slot is within working hours for a given calendar day (local)
  const isTimeSlotAvailable = (timeSlot24, calendarDate = selectedDate) => {
    const dateStr = toLocalYMD(calendarDate);
    const hoursForDay =
      (workingHoursByDate && workingHoursByDate[dateStr]) ||
      (toLocalYMD(selectedDate) === dateStr ? workingHours : []);

    if (!hoursForDay || hoursForDay.length === 0) {
      return false;
    }

    const [slotHour, slotMin] = timeSlot24.split(":").map(Number);
    const slotMinutes = slotHour * 60 + slotMin;

    return hoursForDay.some((wh) => {
      if (!wh.start_time || !wh.end_time) return false;

      const [startHour, startMin] = wh.start_time.split(":").map(Number);
      const [endHour, endMin] = wh.end_time.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (startMinutes <= endMinutes) {
        return slotMinutes >= startMinutes && slotMinutes <= endMinutes;
      }
      return slotMinutes >= startMinutes || slotMinutes <= endMinutes;
    });
  };

  // Convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12) => {
    const [time, period] = time12.split(' ');
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  };

  const getSlotsForCalendarDate = useCallback((calendarDate) => {
    const ymd = toLocalYMD(calendarDate);
    const scheduleSlots = modalTimeSlotsByDate[ymd];
    if (scheduleSlots && scheduleSlots.length > 0) {
      return scheduleSlots;
    }
    return displayTimeSlots.filter((slot) => {
      const time24 = convertTo24Hour(slot);
      return isTimeSlotAvailable(time24, calendarDate);
    });
  }, [modalTimeSlotsByDate, displayTimeSlots, workingHoursByDate, workingHours, selectedDate]);

  const bookableDatesInView = useMemo(() => {
    const viewDates = viewMode === "week"
      ? getWeekDaysHelper()
      : [new Date(selectedDate)];
    const caregiverId = appointmentData.systemCaregiver;

    const datesWithAvailability = viewDates.filter((calendarDate) => {
      const slots = getSlotsForCalendarDate(calendarDate);
      if (!caregiverId) {
        return slots.length > 0;
      }
      return slots.some((slot) =>
        getAppointmentsForSlot(calendarDate, slot, caregiverId).length === 0
      );
    });

    if (appointmentData.appointmentDate) {
      const currentDate = mdyToDate(appointmentData.appointmentDate);
      if (
        currentDate &&
        !datesWithAvailability.some((d) => toLocalYMD(d) === toLocalYMD(currentDate))
      ) {
        datesWithAvailability.push(currentDate);
      }
    }

    return datesWithAvailability.sort((a, b) => a.getTime() - b.getTime());
  }, [
    viewMode,
    selectedDate,
    getWeekDaysHelper,
    appointmentData.systemCaregiver,
    appointmentData.appointmentDate,
    getSlotsForCalendarDate,
    appointments
  ]);

  const modalAvailableTimeSlots = useMemo(() => {
    const caregiverId = appointmentData.systemCaregiver;
    const calendarDate = mdyToDate(appointmentData.appointmentDate);
    if (!calendarDate || !caregiverId) return [];

    const slots = getSlotsForCalendarDate(calendarDate);
    const freeSlots = slots.filter(
      (slot) => getAppointmentsForSlot(calendarDate, slot, caregiverId).length === 0
    );

    if (
      appointmentData.appointmentTime &&
      !freeSlots.includes(appointmentData.appointmentTime)
    ) {
      return [appointmentData.appointmentTime, ...freeSlots];
    }

    return freeSlots;
  }, [
    appointmentData.appointmentDate,
    appointmentData.appointmentTime,
    appointmentData.systemCaregiver,
    getSlotsForCalendarDate,
    appointments
  ]);

  useEffect(() => {
    if (!showAppointmentModal) return;

    const dates = viewMode === "week"
      ? getWeekDaysHelper()
      : [new Date(selectedDate)];

    let cancelled = false;

    const loadModalDateSlots = async () => {
      const responses = await Promise.all(
        dates.map(async (calendarDate) => {
          const ymd = toLocalYMD(calendarDate);
          try {
            const res = await calendarAPI.getAppointmentTimeSchedule(ymd, timeInterval);
            return { ymd, slots: res.success ? res.time_slots || [] : [] };
          } catch {
            return { ymd, slots: [] };
          }
        })
      );

      if (cancelled) return;

      setModalTimeSlotsByDate((prev) => {
        const next = { ...prev };
        responses.forEach(({ ymd, slots }) => {
          if (!next[ymd]) {
            next[ymd] = slots;
          }
        });
        return next;
      });
    };

    void loadModalDateSlots();

    return () => {
      cancelled = true;
    };
  }, [showAppointmentModal, viewMode, selectedDate, timeInterval, getWeekDaysHelper]);

  useEffect(() => {
    if (!showAppointmentModal || !appointmentData.appointmentDate) return;

    const ymd = mdyToLocalYMD(appointmentData.appointmentDate);
    if (!ymd || modalTimeSlotsByDate[ymd]) return;

    let cancelled = false;

    calendarAPI.getAppointmentTimeSchedule(ymd, timeInterval)
      .then((res) => {
        if (cancelled || !res.success) return;
        setModalTimeSlotsByDate((prev) => (
          prev[ymd] ? prev : { ...prev, [ymd]: res.time_slots || [] }
        ));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [showAppointmentModal, appointmentData.appointmentDate, timeInterval, modalTimeSlotsByDate]);

  useEffect(() => {
    if (!showAppointmentModal || modalAvailableTimeSlots.length === 0) return;

    setAppointmentData((prev) => {
      if (prev.appointmentTime && modalAvailableTimeSlots.includes(prev.appointmentTime)) {
        return prev;
      }
      return { ...prev, appointmentTime: modalAvailableTimeSlots[0] };
    });
  }, [
    showAppointmentModal,
    appointmentData.appointmentDate,
    appointmentData.systemCaregiver,
    modalAvailableTimeSlots
  ]);

  return (
    <div className="calendar-page">
      <div className="calendar-page-header">
        <h2 className="dashboard-title">Calendar</h2>
      </div>
      {loading || isInitialScheduleLoading ? (
        <div className="calendar-layout calendar-skeleton-layout">
          <div className="calendar-sidebar">
            <div className="calendar-skeleton-card">
              <div className="calendar-skeleton-line w40" />
              <div className="calendar-skeleton-grid">
                {[...Array(35)].map((_, i) => (
                  <span key={`cal-side-calendar-${i}`} className="calendar-skeleton-cell" />
                ))}
              </div>
            </div>

            <div className="calendar-skeleton-card">
              <div className="calendar-skeleton-line w55" />
              <div className="calendar-skeleton-list">
                {[...Array(12)].map((_, i) => (
                  <span key={`cal-side-upcoming-${i}`} className="calendar-skeleton-line" />
                ))}
              </div>
            </div>
          </div>

          <div className="calendar-main-content">
            <div className="calendar-skeleton-toolbar">
              <span className="calendar-skeleton-line w30" />
              <span className="calendar-skeleton-line w20" />
            </div>

            <div className="calendar-skeleton-table">
              {[...Array(12)].map((_, i) => (
                <div key={`cal-main-row-${i}`} className="calendar-skeleton-row">
                  {[...Array(7)].map((__, j) => (
                    <span key={`cal-main-cell-${i}-${j}`} className="calendar-skeleton-cell" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
      isSystemCaregiver && !canViewAppointments ? (
      <div className="calendar-layout">
        <div className="no-working-hours-message">
          You do not have permission to view calendar.
        </div>
      </div>
      ) : (
      <div className="calendar-layout">
        {/* Left Sidebar */}
        <div className="calendar-sidebar">
          {/* Calendar Component */}
          <div className="sidebar-calendar">
            <div className="calendar-header-nav">
              <button onClick={goToPreviousMonth} className="month-nav-btn">«</button>
              <h3
                className="calendar-month-title clickable"
                onClick={() => {
                  setYearRangeStart(Math.floor(currentMonth.getFullYear() / 10) * 10);
                  setShowYearPicker(false);
                  setShowMonthPicker((prev) => !prev);
                }}
              >
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <button onClick={goToNextMonth} className="month-nav-btn">»</button>
              {(showMonthPicker || showYearPicker) && (
                <div className="month-year-picker" ref={monthYearPickerRef}>
                  {!showYearPicker ? (
                    <>
                      <div
                        className="month-year-picker-year"
                        onClick={() => setShowYearPicker(true)}
                      >
                        {currentMonth.getFullYear()}
                      </div>
                      <div className="month-grid">
                        {shortMonthNames.map((m, idx) => (
                          <button
                            key={m}
                            type="button"
                            className={`month-cell ${currentMonth.getMonth() === idx ? "active" : ""}`}
                            onClick={() => {
                              setCurrentMonth(new Date(currentMonth.getFullYear(), idx, 1));
                              setShowMonthPicker(false);
                            }}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="year-picker-header">
                        <button
                          type="button"
                          className="year-nav-btn"
                          onClick={() => setYearRangeStart((prev) => prev - 10)}
                        >
                          «
                        </button>
                        <span>{yearRangeStart} - {yearRangeStart + 9}</span>
                        <button
                          type="button"
                          className="year-nav-btn"
                          onClick={() => setYearRangeStart((prev) => prev + 10)}
                        >
                          »
                        </button>
                      </div>
                      <div className="year-grid">
                        {decadeYears.map((yr) => (
                          <button
                            key={yr}
                            type="button"
                            className={`year-cell ${currentMonth.getFullYear() === yr ? "active" : ""}`}
                            onClick={() => {
                              setCurrentMonth(new Date(yr, currentMonth.getMonth(), 1));
                              setShowYearPicker(false);
                              setShowMonthPicker(true);
                            }}
                          >
                            {yr}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="calendar-weekdays">
              {weekDays.map((day, index) => {
                const isWeekend = index === 5 || index === 6; // Sa (5) and Su (6)
                return (
                  <div key={day} className={`weekday-label ${isWeekend ? "weekend" : ""}`}>
                    {day}
                  </div>
                );
              })}
            </div>
            <div className="calendar-days-grid">
              {calendarDays.map((date, index) => {
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                return (
                  <div
                    key={index}
                    className={`calendar-day-cell ${!isCurrentMonth(date) ? "other-month" : ""} ${isToday(date) ? "today" : ""} ${isSelected(date) ? "selected" : ""} ${isWeekend ? "weekend" : ""}`}
                    onClick={() => handleDateClick(date)}
                  >
                    {date.getDate()}
                  </div>
                );
              })}
            </div>
            <div className="calendar-actions">
              <button onClick={clearSelection} className="clear-btn">Clear</button>
              <button onClick={goToToday} className="today-btn">Today</button>
            </div>
          </div>

          {/* System Caregivers List — shown for non-system users and for system caregiver with Create permission */}
          {(currentUser?.role_id !== 7 || canCreateAppointment) && (
            <div className="system-caregivers-section">
              <div className="caregivers-header">
                <FontAwesomeIcon icon={faUser} className="caregivers-icon" />
                <h4>System Caregivers</h4>
              </div>
              <div className="caregivers-list">
                {systemCaregivers.length === 0 ? (
                  <div className="no-caregivers">No caregivers available</div>
                ) : (
                  systemCaregivers.map((caregiver) => {
                    const isSelected = selectedCaregivers.map(id => parseInt(id)).includes(parseInt(caregiver.id));
                    return (
                      <label key={caregiver.id} className="caregiver-item">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleCaregiver(caregiver.id)}
                        />
                        <span>
                          {caregiver.last_name}, {caregiver.first_name}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Main Content */}
        <div className="calendar-main-content">
          {/* Controls above appointment table/grid */}
          <div className="calendar-global-controls">
            <div className="controls-left">
              <select
                value={timeInterval}
                onChange={(e) => setTimeInterval(parseInt(e.target.value))}
                className="time-interval-select"
              >
                <option value={15}>15 Minutes</option>
                <option value={30}>30 Minutes</option>
                <option value={45}>45 Minutes</option>
                <option value={60}>60 Minutes</option>
              </select>
              <button
                type="button"
                onClick={() => void refreshScheduleForSelectedRange()}
                className="refresh-btn"
                title="Refresh working hours and appointments"
              >
                <FontAwesomeIcon icon={faRefresh} />
              </button>
            </div>
            <div className="controls-right">
              {viewMode === "week" && (
                <div className="calendar-week-nav" role="group" aria-label="Week navigation">
                  <button
                    type="button"
                    className="calendar-week-nav-btn"
                    title="Previous week"
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(d.getDate() - 7);
                      setSelectedDate(d);
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button
                    type="button"
                    className="calendar-week-nav-btn"
                    title="Next week"
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(d.getDate() + 7);
                      setSelectedDate(d);
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
              <button
                className={`view-toggle-btn ${viewMode === "week" ? "active" : ""}`}
                onClick={() => setViewMode("week")}
              >
                Week
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                List
              </button>
            </div>
          </div>

          {/* List View - Single Day Schedule */}
          {viewMode === "list" && (
            <>
              {/* Combined Date and Caregiver Header for List View */}
              <div className="list-view-header-banner">
                <div className="list-view-date-text">
                  {selectedDate.toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric"
                  })}
                </div>
                {/* Caregiver names removed from here, now displayed in column headers */}
              </div>
              {/* List View Schedule Grid */}
              <div className="list-view-schedule-container" ref={listScheduleContainerRef}>
                {workingHours.length > 0 ? (
                  <div className="list-view-schedule-grid">
                    <div className="list-view-time-column">
                      <div className="list-view-time-header">Time</div>
                      {displayTimeSlots.map((slot, index) => (
                        <div key={index} className="list-view-time-slot">{slot}</div>
                      ))}
                    </div>
                    {selectedCaregiversList.length > 0 ? (
                      selectedCaregiversList.map((caregiver) => (
                        <div key={caregiver.id} className="list-view-schedule-column">
                          <div className="list-view-caregiver-header">
                            {caregiver.last_name}, {caregiver.first_name}
                          </div>
                          {displayTimeSlots.map((slot, index) => {
                            const time24 = convertTo24Hour(slot);
                            const isAvailable = isTimeSlotAvailable(time24, selectedDate);
                            return (
                              <div
                                key={index}
                                className={`list-view-schedule-cell ${isAvailable ? "available" : "unavailable"}`}
                                onClick={() => {
                                  const slotAppointments = getAppointmentsForSlot(selectedDate, slot, caregiver.id);
                                  if (slotAppointments.length > 0) {
                                    if (canViewAppointments) {
                                      openEditAppointmentModal(slotAppointments[0]);
                                    } else {
                                      toast.error("You do not have permission to view appointment details.");
                                    }
                                  } else {
                                    handleTimeSlotClick(slot, caregiver.id, selectedDate);
                                  }
                                }}
                                title={isAvailable ? `Click to book appointment at ${slot}` : "Click to book in non-working hours"}
                              >
                                {getAppointmentsForSlot(selectedDate, slot, caregiver.id).map((apt, idx) => {
                                  const duration = apt.duration || 30;
                                  const startTime24 = normalizeAppointmentTimeTo24(apt.appointment_time);
                                  const startTime12 = convertTo12Hour(startTime24);
                                  const endTime12 = convertTo12Hour(addMinutesToTime24(startTime24, duration));
                                  const tooltipText = [
                                    apt.appointment_type || apt.visit_type_title || '',
                                    `Practice: ${apt.practice_name || 'N/A'}`,
                                    `Caregiver: ${apt.caregiver_last_name || ''}, ${apt.caregiver_first_name || ''}`,
                                    `Patient: ${apt.patient_last_name || ''}, ${apt.patient_first_name || ''}`,
                                    `Visit Type: ${apt.visit_type_title || apt.appointment_type || 'N/A'}`,
                                    `Time: ${startTime12} - ${endTime12}`
                                  ].join('\n');

                                  return (
                                    <div key={idx} className="appointment-badge" title={tooltipText}
                                      style={{ backgroundColor: String(apt.status || "").toLowerCase() === "confirmed" ? "#28a745" : (apt.visit_type_color || apt.color || '#4CAF50') }}>
                                      <span className="appointment-time">{startTime12}</span>
                                      <span className="appointment-patient">{apt.patient_last_name}, {apt.patient_first_name}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      <div className="list-view-no-caregivers">
                        Select caregivers from the left sidebar to view their schedules
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-working-hours-message">
                    No working hours available for this day.
                  </div>
                )}
              </div>
            </>
          )}

          {/* Schedule Grid - Week View (7 Day Columns) */}
          {viewMode === "week" && (
            <div className="schedule-grid-container" ref={weekScheduleContainerRef}>
              {Object.values(workingHoursByDate).some(hours => hours && hours.length > 0) ? (
                <div className="schedule-grid week-view-grid">
                  {/* Caregiver Name Row - Above headers */}
                  {selectedCaregiversList.length > 0 && (
                    <div className="week-caregiver-name-row-full">
                      {selectedCaregiversList.length > 0 ? (
                        <span>{selectedCaregiversList.map(cg => `${cg.last_name}, ${cg.first_name}`).join(" | ")}</span>
                      ) : null}
                    </div>
                  )}

                  {/* Header Row: Time header + Date headers */}
                  <div className="week-header-row">
                    <div className="time-header">Time</div>
                    {selectedCaregiversList.length > 0 && (() => {
                      const weekDays = getWeekDays();
                      return weekDays.map((day, dayIndex) => {
                        const isSelectedDay = day.getDate() === selectedDate.getDate() &&
                          day.getMonth() === selectedDate.getMonth() &&
                          day.getFullYear() === selectedDate.getFullYear();
                        return (
                          <div key={dayIndex} className={`week-day-header ${isSelectedDay ? "selected-day" : ""}`}>
                            {formatDateHeader(day)}
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* Main Grid: Time column + Schedule cells */}
                  <div className="week-main-grid">
                    <div className="time-column">
                      {displayTimeSlots.map((slot, index) => (
                        <div key={index} className="time-slot-label">{slot}</div>
                      ))}
                    </div>
                    {selectedCaregiversList.length > 0 ? (
                      selectedCaregiversList.map((caregiver) => {
                        const weekDays = getWeekDays();
                        return (
                          <div key={caregiver.id} className="caregiver-week-container">
                            {/* Schedule cells for each day */}
                            <div className="week-days-wrapper">
                              {weekDays.map((day, dayIndex) => {
                                return (
                                  <div key={dayIndex} className="day-column-week">
                                    {displayTimeSlots.map((slot, slotIndex) => {
                                      const time24 = convertTo24Hour(slot);
                                      const isAvailable = isTimeSlotAvailable(time24, day);
                                      return (
                                        <div
                                          key={slotIndex}
                                          className={`schedule-cell ${isAvailable ? "available" : "unavailable"}`}
                                          onClick={() => {
                                            setSelectedDate(day);
                                            const slotAppointments = getAppointmentsForSlot(day, slot, caregiver.id);
                                            if (slotAppointments.length > 0) {
                                              if (canViewAppointments) {
                                                openEditAppointmentModal(slotAppointments[0]);
                                              } else {
                                                toast.error("You do not have permission to view appointment details.");
                                              }
                                            } else {
                                              handleTimeSlotClick(slot, caregiver.id, day);
                                            }
                                          }}
                                          title={isAvailable ? `Click to book appointment at ${slot}` : "Click to book in non-working hours"}
                                        >
                                          {getAppointmentsForSlot(day, slot, caregiver.id).map((apt, idx) => {
                                            const dur = apt.duration || 30;
                                            const startT24 = normalizeAppointmentTimeTo24(apt.appointment_time);
                                            const startT = convertTo12Hour(startT24);
                                            const endT = convertTo12Hour(addMinutesToTime24(startT24, dur));
                                            const tip = [
                                              apt.appointment_type || apt.visit_type_title || '',
                                              `Practice: ${apt.practice_name || 'N/A'}`,
                                              `Caregiver: ${apt.caregiver_last_name || ''}, ${apt.caregiver_first_name || ''}`,
                                              `Patient: ${apt.patient_last_name || ''}, ${apt.patient_first_name || ''}`,
                                              `Visit Type: ${apt.visit_type_title || apt.appointment_type || 'N/A'}`,
                                              `Time: ${startT} - ${endT}`
                                            ].join('\n');
                                            return (
                                              <div key={idx} className="appointment-badge" title={tip}
                                                style={{ backgroundColor: String(apt.status || "").toLowerCase() === "confirmed" ? "#28a745" : (apt.visit_type_color || apt.color || '#4CAF50') }}>
                                                <span className="appointment-time">{startT}</span>
                                                <span className="appointment-patient">{apt.patient_last_name}, {apt.patient_first_name}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="no-caregivers-message">
                        Select caregivers from the left sidebar to view their schedules
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-working-hours-message">
                  No working hours available for this week.
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      )
      )}

      {/* Non-Working Hours Confirmation Modal */}
      {showNonWorkingHoursModal && (
        <div className="modal-overlay" onClick={handleCancelNonWorkingHours}>
          <div className="modal-content-confirmation" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-icon">
              <div className="warning-icon-circle">
                <span className="warning-exclamation">!</span>
              </div>
            </div>
            <h3 className="confirmation-title">Are you sure?</h3>
            <p className="confirmation-message">
              you want to proceed the scheduling in non working hours?
            </p>
            <div className="confirmation-buttons">
              <button
                type="button"
                className="btn-confirm-yes"
                onClick={handleConfirmNonWorkingHours}
              >
                Yes
              </button>
              <button
                type="button"
                className="btn-confirm-no"
                onClick={handleCancelNonWorkingHours}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Book Appointment Modal */}
      {showAppointmentModal && (
        <AddForm
          show={showAppointmentModal}
          title="Book Appointment"
          onClose={closeAppointmentModal}
          footerActions={
            <div className="modal-footer add-practice-form-actions">
              <button
                type="button"
                className="add-practice-btn add-practice-btn-cancel"
                onClick={closeAppointmentModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="add-practice-btn add-practice-btn-add"
                onClick={handleCreateAppointment}
              >
                Create
              </button>
            </div>
          }
        >
          <div className="appointment-form-grid">
                  {/* Left Column */}
                  <div className="appointment-form-left-column">
                    <div className="form-group">
                      <label>Practices: <span className="required">*</span></label>
                      <select
                        className="form-control"
                        value={appointmentData.practice}
                        onChange={(e) => handlePracticeChangeInModal(e.target.value)}
                        disabled={currentUser?.role_id === 1 && !appointmentData.systemCaregiver}
                      >
                        <option value="">--select--</option>
                        {practices.map((practice) => (
                          <option key={practice.id} value={practice.id}>
                            {practice.practice_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>System CareGiver: <span className="required">*</span></label>
                      <select
                        className="form-control"
                        value={appointmentData.systemCaregiver}
                        onChange={(e) => handleSystemCaregiverChangeInModal(e.target.value)}
                        disabled={currentUser?.role_id === 7 || Boolean(selectedTimeSlot?.caregiverId)}
                      >
                        {currentUser?.role_id !== 7 && <option value="">--select--</option>}
                        {(currentUser?.role_id === 7
                          ? systemCaregivers.filter((cg) => String(cg.id) === String(currentUser?.id))
                          : systemCaregivers
                        ).map((cg) => (
                          <option key={cg.id} value={cg.id}>
                            {cg.last_name}, {cg.first_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Visit Type: <span className="required">*</span></label>
                      <select
                        className="form-control"
                        value={appointmentData.visitType}
                        onChange={(e) => handleVisitTypeChange(e.target.value)}
                        disabled={!appointmentData.practice || loadingPracticeData || visitTypes.length === 0}
                      >
                        <option value="">--select--</option>
                        {visitTypes.length > 0 ? (
                          visitTypes.map((vt) => (
                            <option key={vt.id} value={String(vt.id)}>
                              {vt.title}
                            </option>
                          ))
                        ) : appointmentData.practice && !loadingPracticeData ? (
                          <option value="" disabled>
                            No visit types for this practice
                          </option>
                        ) : (
                          <option value="" disabled>
                            {appointmentData.practice ? "Loading visit types…" : "Select a practice first"}
                          </option>
                        )}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Visit Duration: <span className="required">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={appointmentData.visitDuration}
                        onChange={(e) => setAppointmentData({ ...appointmentData, visitDuration: e.target.value })}
                        placeholder="Duration in minutes"
                        readOnly={!!appointmentData.visitType}
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="appointment-form-right-column">
                    <div className="form-group">
                      <label>Reason for Appointment: <span className="required">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={appointmentData.reasonForAppointment}
                        onChange={(e) => setAppointmentData({ ...appointmentData, reasonForAppointment: e.target.value })}
                        placeholder="Enter reason for appointment"
                      />
                    </div>
                    <div className="form-group">
                      <label>Patients: <span className="required">*</span></label>
                      <div className="patient-dropdown-wrapper" ref={patientDropdownRef}>
                        <div
                          className={`patient-dropdown-box ${!appointmentData.practice || loadingPracticeData ? 'disabled' : ''}`}
                          onClick={() => {
                            if (appointmentData.practice && !loadingPracticeData) {
                              setIsPatientDropdownOpen(!isPatientDropdownOpen);
                            }
                          }}
                        >
                          <input
                            type="search"
                            className="patient-search-input"
                            placeholder={
                              !appointmentData.practice
                                ? "Select practice first"
                                : loadingPracticeData
                                  ? "Loading patients..."
                                  : appointmentData.patient
                                    ? filteredPatients.find(p => p.id.toString() === appointmentData.patient)
                                      ? `${filteredPatients.find(p => p.id.toString() === appointmentData.patient).first_name} ${filteredPatients.find(p => p.id.toString() === appointmentData.patient).last_name}`
                                      : "Select patient"
                                    : "Search or select patient..."
                            }
                            value={isPatientDropdownOpen ? patientSearchTerm : (appointmentData.patient ? (filteredPatients.find(p => p.id.toString() === appointmentData.patient) ? `${filteredPatients.find(p => p.id.toString() === appointmentData.patient).first_name} ${filteredPatients.find(p => p.id.toString() === appointmentData.patient).last_name}` : "") : "")}
                            onChange={(e) => {
                              setPatientSearchTerm(e.target.value);
                              if (!isPatientDropdownOpen) {
                                setIsPatientDropdownOpen(true);
                              }
                            }}
                            onFocus={() => {
                              if (appointmentData.practice && !loadingPracticeData) {
                                setIsPatientDropdownOpen(true);
                              }
                            }}
                            disabled={!appointmentData.practice || loadingPracticeData}
                            readOnly={!isPatientDropdownOpen}
                          />
                          <span className="patient-dropdown-arrow">
                            {isPatientDropdownOpen ? "▲" : "▼"}
                          </span>
                        </div>
                        {isPatientDropdownOpen && appointmentData.practice && !loadingPracticeData && (
                          <div className="patient-dropdown-list">
                            {filteredPatients.length === 0 ? (
                              <div className="patient-dropdown-item disabled">
                                {patientSearchTerm.trim() ? "No patients found" : "No patients available"}
                              </div>
                            ) : (
                              filteredPatients.map((patient) => (
                                <div
                                  key={patient.id}
                                  className={`patient-dropdown-item ${appointmentData.patient === patient.id.toString() ? "selected" : ""}`}
                                  onClick={() => {
                                    setAppointmentData({ ...appointmentData, patient: patient.id.toString() });
                                    setIsPatientDropdownOpen(false);
                                    setPatientSearchTerm("");
                                  }}
                                >
                                  {patient.first_name} {patient.last_name}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Appointment Date: <span className="required">*</span></label>
                      <div className="appointment-date-input-wrapper">
                        <select
                          className="appointment-date-input appointment-date-select"
                          value={appointmentData.appointmentDate}
                          onChange={(e) => {
                            setAppointmentData({
                              ...appointmentData,
                              appointmentDate: e.target.value,
                              appointmentTime: ""
                            });
                          }}
                          disabled={bookableDatesInView.length <= 1}
                        >
                          {bookableDatesInView.length === 0 ? (
                            <option value={appointmentData.appointmentDate || ""}>
                              {appointmentData.appointmentDate || "No dates available"}
                            </option>
                          ) : (
                            bookableDatesInView.map((calendarDate) => {
                              const value = toAppointmentFormDate(calendarDate);
                              return (
                                <option key={toLocalYMD(calendarDate)} value={value}>
                                  {formatDateHeader(calendarDate)}
                                </option>
                              );
                            })
                          )}
                        </select>
                        <span className="appointment-date-calendar-btn" aria-hidden="true">
                          <FontAwesomeIcon icon={faCalendar} />
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Appointment Time: <span className="required">*</span></label>
                      <select
                        className="form-control"
                        value={appointmentData.appointmentTime}
                        onChange={(e) => setAppointmentData({ ...appointmentData, appointmentTime: e.target.value })}
                        disabled={!appointmentData.systemCaregiver || modalAvailableTimeSlots.length === 0}
                      >
                        <option value="">
                          {!appointmentData.systemCaregiver
                            ? "Select caregiver first"
                            : modalAvailableTimeSlots.length === 0
                              ? "No slots available"
                              : "--select--"}
                        </option>
                        {modalAvailableTimeSlots.map((slot) => (
                          <option key={slot} value={slot}>
                            {slot}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
          </div>
        </AddForm>
      )}

      {/* Edit Appointment Modal (confirmation update) */}
      {showEditAppointmentModal && editAppointmentData && (
        <AddForm
          show={showEditAppointmentModal}
          title="Appointment Details"
          onClose={closeEditAppointmentModal}
          footerActions={
            <div className="modal-footer add-practice-form-actions">
              <button
                type="button"
                className="add-practice-btn add-practice-btn-cancel"
                onClick={closeEditAppointmentModal}
              >
                Cancel
              </button>
              {canDeleteAppointment && (
                <button
                  type="button"
                  className="add-practice-btn add-practice-btn-cancel"
                  onClick={handleDeleteAppointment}
                >
                  Delete
                </button>
              )}
              {canEditAppointment && (
                <button
                  type="button"
                  className="add-practice-btn add-practice-btn-add"
                  onClick={handleUpdateAppointment}
                >
                  Save
                </button>
              )}
            </div>
          }
        >
          <div className="appointment-form-grid">
            <div className="appointment-form-left-column">
              <div className="form-group">
                <label>Practice</label>
                <input className="form-control" value={editingAppointment?.practice_name || "-"} readOnly />
              </div>
              <div className="form-group">
                <label>System Caregiver</label>
                <input className="form-control" value={`${editingAppointment?.caregiver_last_name || ""}, ${editingAppointment?.caregiver_first_name || ""}`.replace(/^,\s*/, "")} readOnly />
              </div>
              <div className="form-group">
                <label>Visit Type</label>
                <input className="form-control" value={editingAppointment?.visit_type_title || editingAppointment?.appointment_type || "-"} readOnly />
              </div>
              <div className="form-group">
                <label>Visit Duration</label>
                <input className="form-control" value={editAppointmentData.visitDuration || "30"} readOnly />
              </div>
            </div>
            <div className="appointment-form-right-column">
              <div className="form-group">
                <label>Patient</label>
                <input className="form-control" value={`${editingAppointment?.patient_last_name || ""}, ${editingAppointment?.patient_first_name || ""}`.replace(/^,\s*/, "")} readOnly />
              </div>
              <div className="form-group">
                <label>Appointment Date</label>
                <input className="form-control" value={editAppointmentData.appointmentDate || ""} readOnly />
              </div>
              <div className="form-group">
                <label>Appointment Time</label>
                <input className="form-control" value={editAppointmentData.appointmentTime || ""} readOnly />
              </div>
              <div className="form-group">
                <label>Confirmation</label>
                <select
                  className="form-control"
                  value={editAppointmentData.confirmation}
                  disabled={!canEditAppointment}
                  onChange={(e) =>
                    setEditAppointmentData((prev) => ({
                      ...prev,
                      confirmation: e.target.value
                    }))
                  }
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </div>
        </AddForm>
      )}
    </div>
  );
};

export default Calendar;
